import React, { useCallback, useState, useRef, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import { ButtonGhost } from "oolib/dist/v2/components/Buttons";
import TooltipV2 from "@/components/UI/Tooltip";
import { DataFieldInputSelector } from "@/components/DataFieldInputSelector";
import { SearchableLightbox } from "@/components/DataFieldInputSelector/SearchableLightbox";
import { useBarChartConfig } from "./ConfigForm_Context";
import { getPickerProp } from "../../utils/pickerVariantConfig";
import { DATA_KEYS } from "./DATA_KEYS";
import { CollapseTransitionItem } from "../../utils/CollapseTransition";
import { useReportLandingContext } from "../../../../ReportLandingContext";
import { getHiddenDim1ForPreset } from "../utils/getTopBottomDatapoints";
import { useVisibilityModifier } from "../../utils/useVisibilityModifier";

export const ConfigForm_Dimension1Section = () => {
  const {
    widgetConfig,
    dataKeys,
    setConfig,
    updateWidget,
    underEditWidgetIdx,
    optionsForDim1,
    arithmeticState,
  } = useBarChartConfig();

  const {
    dataFields,
    sortedLetters,
    numberInputOptions,
    handleFieldAChange,
    handleFieldChange: handleArithFieldChange,
    handleRemoveField,
  } = arithmeticState;

  const { widgetDataStore } = useReportLandingContext();
  const data = widgetDataStore?.[widgetConfig.id];

  // onChange for field A
  const handleDim1FieldAChange = useCallback((id, v) => {
    if (!v) { handleFieldAChange(null); return; }
    handleFieldAChange(v);
  }, [handleFieldAChange]);

  const isNumberInput = dataFields.A?.comp === 'NumberInput';

  const updateConfig = useCallback((updaterFn) => {
    setConfig((prev) => {
      let w = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      w = updaterFn(w);
      const full = [...prev];
      full[underEditWidgetIdx] = w;
      return full;
    });
  }, [setConfig, underEditWidgetIdx]);

  // Show/Hide (eye) for non-number dim1 fields; number fields get theirs from
  // NumberFieldConfig instead.
  const visibilityModifier = useVisibilityModifier({
    dimension: 1,
    widgetConfig,
    dataKeys: DATA_KEYS,
    data,
    isNumberInput,
    updateConfig,
    getHiddenForPreset: getHiddenDim1ForPreset,
  });

  return (
    <>
      {/* Field rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <AnimatePresence initial={false}>
          {sortedLetters.map((letter) => {
            const field = (
              <DataFieldInputSelector
                key={letter}
                indicator={sortedLetters.length > 1 ? { label: letter } : undefined}
                options={letter === 'A' ? optionsForDim1 : numberInputOptions}
                value={dataFields[letter]}
                onChange={(id, v) => {
                  if (letter === 'A') {
                    handleDim1FieldAChange(id, v);
                  } else {
                    handleArithFieldChange(letter, v);
                  }
                }}
                actions={sortedLetters.length > 1 ? [{ icon: 'X', onClick: () => handleRemoveField(letter), hoverColor: 'red' }] : []}
                placeholder="Select field"
                modifier={letter === 'A' ? visibilityModifier : undefined}
                picker={getPickerProp({
                  widgetComp: 'WidgetBarChart',
                  slot: letter === 'A' ? 'dimension1' : 'numberFields',
                })}
              />
            );
            if (letter === 'A') return field;
            return (
              <CollapseTransitionItem key={letter} id={letter}>
                {field}
              </CollapseTransitionItem>
            );
          })}
        </AnimatePresence>
      </div>
    </>
  );
};

/** Exposed for ConfigForm/index.js to place in SectionWrapper headerRight */
export const Dimension1AddFieldButton = () => {
  const { arithmeticState } = useBarChartConfig();
  const { canAddFields, handleAddField, numberInputOptions, dataFields } = arithmeticState;
  const [showLightbox, setShowLightbox] = useState(false);
  const btnRef = useRef(null);

  // Values already used by fields A, B, C... — so we don't offer them again.
  const addedValues = useMemo(() => {
    const set = new Set();
    Object.values(dataFields).forEach((f) => {
      if (f?.value != null) set.add(f.value);
    });
    return set;
  }, [dataFields]);

  // numberInputOptions minus already-added fields. Preserves the flat/grouped shape and
  // drops any group that becomes empty after filtering.
  const availableOptions = useMemo(() => {
    const isGrouped = numberInputOptions.some(item => Array.isArray(item.options));
    if (!isGrouped) {
      return numberInputOptions.filter((o) => !addedValues.has(o.value));
    }
    return numberInputOptions
      .map((group) =>
        Array.isArray(group.options)
          ? { ...group, options: group.options.filter((o) => !addedValues.has(o.value)) }
          : group
      )
      .filter((group) => !Array.isArray(group.options) || group.options.length > 0);
  }, [numberInputOptions, addedValues]);

  const hasAvailableOptions = availableOptions.length > 0;
  const canAdd = canAddFields && hasAvailableOptions;

  return (
    <>
      <TooltipV2
        text={
          !dataFields.A
            ? "Add data to visualize"
            : !canAddFields
              ? "Activates only on number fields for arithmetic aggregation"
              : !hasAvailableOptions
                ? "No other compatible fields are available"
                : "Add additional number fields to include in this calculation"
        }
        placement="top"
      >
        <span ref={btnRef}>
          <ButtonGhost
            icon="Plus"
            onClick={() => setShowLightbox(true)}
            disabled={!canAdd}
            style={{ padding: "4px" }}
          />
        </span>
      </TooltipV2>
      {showLightbox && (
        <SearchableLightbox
          anchorRef={btnRef}
          onClose={() => setShowLightbox(false)}
          options={availableOptions}
          grouped={availableOptions.some(item => item.options && Array.isArray(item.options))}
          value={undefined}
          onChange={(_, selectedField) => {
            let enriched = selectedField;
            if (selectedField && !selectedField.categoryLabel) {
              const group = availableOptions.find(g =>
                Array.isArray(g.options) && g.options.some(o => o.value === selectedField.value)
              );
              if (group) enriched = { ...selectedField, categoryLabel: group.display };
            }
            handleAddField(enriched);
            setShowLightbox(false);
          }}
          searchPlaceholder="Search fields"
        />
      )}
    </>
  );
};
